import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@nextui-org/input";
import {
  clearInputError,
  validateEachType,
  validateEachTypeQuantity,
} from "../../utils/validateEachType";
import { onKeyDown } from "../../utils/onKeyDownFunction";
import messages from "../../errorTypes/Types.ts";

const DwuteownikAlu = ({ onWeightChange }) => {
  const [length, setLength] = useState("");
  const [type, setType] = useState("");
  const [data, setData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [count, setCount] = useState(1);

  const [errorLength, setErrorLength] = useState(null);
  const [errorCount, setErrorCount] = useState(null);

  const fetchData = useCallback(() => {
    let url;
    switch (type) {
      case "I":
        url = "data/dwuteowniki-i.json";
        break;
      case "IPE":
        url = "data/dwuteowniki-ipe.json";
        break;
      case "HE":
        url = "data/dwuteowniki-he.json";
        break;
      default:
        url = "";
    }

    if (url) {
      fetch(url)
        .then((response) => response.json())
        .then((data) => setData(data))
        .catch((error) => console.error(error));
    } else {
      setData([]);
    }
  }, [type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedOption) {
      const item = data.find((item) => item.Oznaczenie === selectedOption);
      setSelectedItem(item);
    } else {
      setSelectedItem(null);
    }
  }, [selectedOption, data]);

  const calculateWeight = useCallback(() => {
    if (selectedItem && selectedItem.waga * count !== null) {
      const formattedLength = length.replace(",", ".");
      onWeightChange({
        totalWeight: (selectedItem.waga * Number(formattedLength)).toFixed(3),
        totalWeightPerKg: selectedItem.waga.toFixed(3),
      });
    } else {
      onWeightChange({
        totalWeight: "0.000",
        totalWeightPerKg: "0.000",
      });
    }
  }, [selectedItem, length, count, onWeightChange]);

  useEffect(() => {
    const handler = setTimeout(() => {
      calculateWeight();
    }, 100); // Opóźnienie 100ms

    return () => {
      clearTimeout(handler);
    };
  }, [calculateWeight]);

  return (
    <div className="flex flex-col w-full gap-4 items-start sm:w-full lg:w-full">
      <div className="flex flex-col w-full items-start sm:w-full lg:w-full">
        <div className="flex flex-col items-start justify-start h-[9em] gap-4 w-full sm:flex sm:flex-col sm:items-start sm:gap-4 sm:h-[9em] sm:w-full lg:flex lg:flex-col lg:items-start lg:justify-start lg:gap-3 lg:h-[9em]">
          <select
            name="dwuteowniki"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded-md text-[#919191] bg-[#fff] px-4 py-4 sm:w-full sm:rounded-md sm:px-4 sm:py-4 lg:py-3;"
          >
            <option value="">Wybierz typ</option>
            <option value="I">Dwuteowniki IPN</option>
            <option value="IPE">Dwuteowniki IPE</option>
            <option value="HE">Dwuteowniki HE</option>
          </select>

          <select
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
            name="rodzaj"
            className="w-full rounded-md text-[#919191] bg-[#fff] px-4 py-4 sm:w-full sm:rounded-md sm:px-4 sm:py-4 lg:py-3;"
          >
            <option value="">Wybierz rodzaj dwuteownika</option>
            {data.map((item, index) => (
              <option key={index} value={item.Oznaczenie}>
                {item.Oznaczenie}
              </option>
            ))}
          </select>
        </div>
        <div className="obliczenia-inputs w-full">
          <label htmlFor="length-input">
            [D] Wpisz długość (m)
          </label>
          <Input
            id="length-input"
            type="text"
            autoComplete="off"
            value={length}
            classNames={{
              inputWrapper:
                "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0 w-full",
              input: "h-9 w-full",
            }}
            onChange={(e) => {
              const formattedValue = e.target.value.replace(".", ",");
              const integerPart = formattedValue.split(",")[0];
              const decimalPart = formattedValue.split(",")[1];
              if (integerPart.length > 6) {
                setErrorLength(messages.unicError.maxInteger);
                return;
              }
              if (decimalPart && decimalPart.length > 3) {
                setErrorLength(messages.unicError.maxDecimal);
                return;
              }
              if (!formattedValue.trim()) {
                setErrorLength(messages.unicError.empty);
                return;
              }
              if (!validateEachType(formattedValue)) {
                setErrorLength(messages.unicError.invalid);
                return;
              }
              setLength(formattedValue);
              setErrorLength(null);
            }}
            onBlur={() => {
              if (!validateEachType(length)) {
                setErrorLength(messages.unicError.empty);
                return;
              }
              clearInputError(length, setErrorLength);
            }}
            onKeyDown={(e) => onKeyDown(e, setLength)}
          />
          {errorLength && <p className="text-error-text">{errorLength}</p>}
        </div>
        <div className="obliczenia-inputs w-full">
          <label htmlFor="count-input">
            Wpisz ilość (szt.)
          </label>
          <Input
            id="count-input"
            type="text"
            autoComplete="off"
            value={count}
            classNames={{
              inputWrapper:
                "bg-transparent rounded-none group-data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent px-0 w-full",
              input: "h-9 w-full",
            }}
            onChange={(e) => {
              const inputValue = e.target.value.trim();
              const formattedValue =
                inputValue === null || inputValue === undefined
                  ? "0"
                  : inputValue;
              if (formattedValue.length > 6) {
                setErrorCount(messages.unicErrorQuantity.maxInteger);
                return;
              }
              if (!formattedValue.trim()) {
                setErrorCount(messages.unicErrorQuantity.empty);
                return;
              }
              if (!validateEachTypeQuantity(formattedValue)) {
                setErrorCount(messages.unicErrorQuantity.invalid);
                return;
              }
              setCount(formattedValue);
              setErrorCount(null);
            }}
            onBlur={() => {
              if (!validateEachTypeQuantity(count)) {
                setErrorCount(messages.unicErrorQuantity.empty);
                return;
              }
              clearInputError(count, setErrorCount);
            }}
            onKeyDown={(e) => onKeyDown(e, setCount)}
          />
          {errorCount && <p className="text-error-text">{errorCount}</p>}
        </div>
      </div>
      {selectedItem && (
        <div className="w-full">
          <p>
            Oznaczenie:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.Oznaczenie}</span>
            <br />
            Wysokość:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.wysokosc} mm</span>
            <br />
            Szerokość:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.szerokosc} mm</span>
            <br />
            Grubość:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.grubosc} mm</span>
            <br />
            Ścianka:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.scianka} mm</span>
            <br />
            Łączenie:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.laczenie} mm</span>
            <br />
            Waga:{" "}
            <span style={{ fontWeight: "600" }}>{selectedItem.waga} kg/m</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default DwuteownikAlu;